import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Invoicing read/generate DTOs (admin + finance). Read models over the
 * `invoice` / `invoice_line` / `invoice_tax_line` tables minted by the
 * invoice-generation engine.
 *
 * Money is `BigInt` paise in the DB. BigInt has no JSON representation, so
 * every money field is serialised to a decimal STRING on the wire (mirrors how
 * the tbo-audit row serialises its BigInt id). Rates/percentages are Prisma
 * `Decimal`, likewise serialised to string.
 */

// ----- Enums (mirror the Prisma enums) -----

export const InvoiceStatusSchema = z.enum(['PROFORMA', 'TAX_INVOICE', 'CANCELLED']);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

export const GstSlabSchema = z.enum(['PKG', 'CRP', 'B2C', 'B2CF']);
export type GstSlab = z.infer<typeof GstSlabSchema>;

export const GstPlaceOfSupplySchema = z.enum(['INTRA', 'INTER', 'EXPORT']);
export type GstPlaceOfSupply = z.infer<typeof GstPlaceOfSupplySchema>;

// ----- List query -----

export const InvoiceListQuerySchema = PaginationQuerySchema.extend({
  /** Comma-separated InvoiceStatus values (multi-select filter). */
  status: z.string().trim().optional(),
  /** Comma-separated GstSlab values (multi-select filter). */
  gstSlab: z.string().trim().optional(),
  /** Comma-separated GstPlaceOfSupply values (multi-select filter). */
  placeOfSupply: z.string().trim().optional(),
  /** Free-text search across invoice number / proforma / tax number, customer
   *  name / email / mobile, agent name, GSTIN. */
  q: z.string().trim().optional(),
  /** Comma-separated agent names (multi-select filter). */
  travelAgentName: z.string().trim().optional(),
  /** Comma-separated company bank-account ids the invoice prints (multi-select). */
  bankAccountId: z.string().trim().optional(),
  /** B2B = buyer has a GSTIN; B2C = no GSTIN. */
  customerType: z.enum(['B2B', 'B2C']).optional(),
  customerId: UuidSchema.optional(),
  bookingId: UuidSchema.optional(),
  /** Inclusive lower bound on createdAt (ISO datetime). */
  from: IsoDateTimeSchema.optional(),
  /** Inclusive upper bound on createdAt (ISO datetime). */
  to: IsoDateTimeSchema.optional(),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuerySchema>;

// ----- Row DTO -----

export const InvoiceRowSchema = z.object({
  id: UuidSchema,
  invoiceNumber: z.string(),
  status: InvoiceStatusSchema,
  gstSlab: GstSlabSchema,
  placeOfSupply: GstPlaceOfSupplySchema,
  /** Null for invoices created directly from a form (no booking). */
  bookingId: UuidSchema.nullable(),
  customerId: UuidSchema,
  /** Resolved customer display name (`Customer.displayName`), null when unresolved. */
  customerName: z.string().nullable(),
  /** BigInt paise serialised as a decimal string. */
  grossSalePaise: z.string(),
  cgstPaise: z.string(),
  sgstPaise: z.string(),
  igstPaise: z.string(),
  tcsPaise: z.string(),
  grandTotalPaise: z.string(),
  /** Proforma number (minted at create), null only for legacy rows. */
  proformaNumber: z.string().nullable(),
  /** Tax-invoice number, null until conversion on full payment. */
  taxInvoiceNumber: z.string().nullable(),
  /** Sum of all recorded receipts, paise as a decimal string. */
  amountPaidPaise: z.string(),
  /** Grand total minus amount paid (floored at 0), paise as a decimal string. */
  amountDuePaise: z.string(),
  financialYear: z.string(),
  createdAt: IsoDateTimeSchema,
  /** Agent named on the invoice (free text), null when none. */
  travelAgentName: z.string().nullable(),
  /** Buyer/customer GSTIN, null for retail. */
  buyerGstin: z.string().nullable(),
  /** Trip details (also shown on the printed particulars box). */
  departure: z.string().nullable(),
  destination: z.string().nullable(),
  departureDate: IsoDateTimeSchema.nullable(),
  returnDate: IsoDateTimeSchema.nullable(),
  noOfPax: z.number().int().nullable(),
  /** Total supplier cost booked against the invoice, paise as a decimal string. */
  supplierCostPaise: z.string(),
  /** Gross margin = pre-GST sale minus supplier cost, paise as a decimal string (may be negative). */
  profitPaise: z.string(),
  /** Enrichment fields (also flow into the Excel export). */
  journeyType: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
  region: z.string().nullable(),
  querySource: z.string().nullable(),
  hotelName: z.string().nullable(),
  hsn: z.string().nullable(),
});
export type InvoiceRow = z.infer<typeof InvoiceRowSchema>;

// ----- Detail DTO (row + lines + tax lines + entity) -----

export const InvoiceLineSchema = z.object({
  description: z.string(),
  hsn: z.string(),
  quantity: z.number().int(),
  /** BigInt paise serialised as a decimal string. */
  amountPaise: z.string(),
});
export type InvoiceLine = z.infer<typeof InvoiceLineSchema>;

export const InvoiceTaxLineSchema = z.object({
  taxType: z.string(),
  /** Prisma Decimal serialised as a string. */
  ratePercent: z.string(),
  /** BigInt paise serialised as a decimal string. */
  amountPaise: z.string(),
});
export type InvoiceTaxLine = z.infer<typeof InvoiceTaxLineSchema>;

export const InvoiceBillingEntitySchema = z.object({
  legalName: z.string(),
  gstin: z.string(),
  pan: z.string().nullable(),
  cin: z.string().nullable(),
  logoUrl: z.string().nullable(),
  /** Company round-stamp / seal image printed near the signature box. */
  sealUrl: z.string().nullable(),
  /** Terms & conditions printed at the bottom of the invoice. */
  terms: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  stateName: z.string().nullable(),
  stateCode: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});
export type InvoiceBillingEntity = z.infer<typeof InvoiceBillingEntitySchema>;

/** Seller's bank account block (first active account of the billing entity). */
export const InvoiceBankSchema = z.object({
  bankName: z.string(),
  accountNumber: z.string(),
  ifsc: z.string().nullable(),
  branchName: z.string().nullable(),
  label: z.string(),
});
export type InvoiceBank = z.infer<typeof InvoiceBankSchema>;

/** Buyer block (invoice Customer + its first address). */
export const InvoiceBuyerSchema = z.object({
  name: z.string(),
  gstNumber: z.string().nullable(),
  addressLine1: z.string().nullable(),
  city: z.string().nullable(),
  stateName: z.string().nullable(),
  stateCode: z.string().nullable(),
});
export type InvoiceBuyer = z.infer<typeof InvoiceBuyerSchema>;

export const InvoiceDetailSchema = InvoiceRowSchema.extend({
  /** When the tax invoice was issued (null while still a proforma). */
  issuedAt: IsoDateTimeSchema.nullable(),
  sellerStateCode: z.string().nullable(),
  buyerStateCode: z.string().nullable(),
  hsn: z.string(),
  taxableMarginPaise: z.string(),
  /** Input tax credit (sum of supplier GST) - internal profit figure, paise string. */
  totalItcPaise: z.string(),
  /** Whether the printed invoice shows the CGST/SGST/IGST bifurcation. */
  showGstBifurcation: z.boolean(),
  passengers: z.array(
    z.object({
      title: z.string().nullable(),
      firstName: z.string(),
      lastName: z.string().nullable(),
      paxType: z.enum(['ADT', 'CHD', 'INF']),
    }),
  ),
  /** Selected travel services (Flight / Hotel / Transfer / Visa / ...). */
  products: z.array(z.object({ code: z.string(), name: z.string() })),
  /** Flight route legs (multi-city). */
  flightSectors: z.array(
    z.object({
      departureCity: z.string().nullable(),
      destinationCity: z.string().nullable(),
      departureDate: IsoDateTimeSchema.nullable(),
    }),
  ),
  /** Per-pax airline ticket rows. */
  airlineSectors: z.array(
    z.object({
      paxName: z.string(),
      airlineName: z.string().nullable(),
      pnr: z.string().nullable(),
      eTicket: z.string().nullable(),
      flightType: z.string().nullable(),
    }),
  ),
  /** Train / bus booking rows. */
  trainBusSectors: z.array(
    z.object({
      pnr: z.string().nullable(),
      bookingClass: z.string().nullable(),
      quota: z.string().nullable(),
    }),
  ),
  lines: z.array(InvoiceLineSchema),
  taxLines: z.array(InvoiceTaxLineSchema),
  billingEntity: InvoiceBillingEntitySchema,
  /** Seller's first bank (kept for back-compat); prefer `banks`. */
  bank: InvoiceBankSchema.nullable(),
  /**
   * Bank accounts to print on the invoice: the one chosen on the invoice if set,
   * otherwise every active bank of the GST account (so both ICICI + HDFC show by
   * default). Resolved live, so editing a bank in settings reflects here.
   */
  banks: z.array(InvoiceBankSchema),
  /** Buyer derived from the invoice customer + first address, or null. */
  buyer: InvoiceBuyerSchema.nullable(),
});
export type InvoiceDetail = z.infer<typeof InvoiceDetailSchema>;

// ----- List response -----

export const InvoiceListResponseSchema = paginatedResponseSchema(InvoiceRowSchema);
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;

// ----- Generate request -----

/**
 * Generate a proforma from a booking. Callers may pass the internal booking
 * `bookingId` (uuid) or the human-facing `bookingNumber` (e.g. `BK-2026-...`);
 * at least one is required. The service resolves a bookingNumber to its id.
 */
export const InvoiceGenerateRequestSchema = z
  .object({
    bookingId: UuidSchema.optional(),
    bookingNumber: z.string().trim().min(1).optional(),
  })
  .refine((v) => Boolean(v.bookingId) || Boolean(v.bookingNumber), {
    message: 'Provide bookingId or bookingNumber',
  });
export type InvoiceGenerateRequest = z.infer<typeof InvoiceGenerateRequestSchema>;

// ----- Generate response (mirrors the service return) -----

export const InvoiceGenerateResponseSchema = z.object({
  id: UuidSchema,
  invoiceNumber: z.string(),
  /** Supplier changes queued for FINANCE/ADMIN approval by an invoice edit. */
  pendingSupplierChanges: z.number().int().optional(),
});
export type InvoiceGenerateResponse = z.infer<typeof InvoiceGenerateResponseSchema>;

// ----- Direct create request (legacy createinvoicewithgst parity) -----

/**
 * Create an invoice DIRECTLY from a form, with no pre-existing booking - a port
 * of the legacy `createinvoicewithgst` screen. Accounts pick a customer, choose
 * the GST slab EXPLICITLY (and optionally the place of supply), enter the sale
 * amount, add supplier cost lines (which sum to the supplier cost) and
 * passengers, and save. The GST margin back-solve then carves CGST/SGST/IGST out
 * of (sale - supplierCost).
 *
 * Money is rupees*100 as a BigInt-safe decimal STRING (e.g. `1180000` = Rs
 * 11,800.00) to survive JSON, mirroring the read-side serialisation.
 */
export const CreateInvoiceRequestSchema = z.object({
  customerId: UuidSchema,
  /** DOM (domestic) / INT (international); used to derive place-of-supply. */
  productType: z.enum(['DOM', 'INT']).optional(),
  /** EXPLICIT slab (PKG/CRP/B2C/B2CF) - accounts choose it, we do not derive. */
  gstSlab: GstSlabSchema,
  /** Optional override; otherwise derived from seller/buyer state + domesticity. */
  placeOfSupply: GstPlaceOfSupplySchema.optional(),
  /** Gross sale amount (customer-payable, GST carved out) in paise. */
  saleAmountPaise: z.string().regex(/^\d+$/),
  supplierLines: z
    .array(
      z.object({
        supplierId: UuidSchema.optional(),
        description: z.string().optional(),
        amountPaise: z.string().regex(/^\d+$/),
        commissionPaise: z.string().regex(/^\d+$/).optional(),
        tdsPaise: z.string().regex(/^\d+$/).optional(),
      }),
    )
    .default([]),
  passengers: z
    .array(
      z.object({
        firstName: z.string().min(1),
        lastName: z.string().optional(),
        paxType: z.enum(['ADT', 'CHD', 'INF']).default('ADT'),
      }),
    )
    .default([]),
  lineDescription: z.string().optional(),
  noOfPax: z.number().int().optional(),
});
export type CreateInvoiceRequest = z.infer<typeof CreateInvoiceRequestSchema>;

// ----- Live GST preview (POST /invoices/preview) -----

/**
 * Live GST preview request - powers the create-invoice form's real-time
 * CGST/SGST/IGST display. Runs the same pricing engine as create, with NO DB
 * write. Money is paise as a decimal STRING to survive JSON.
 */
export const InvoicePreviewRequestSchema = z.object({
  /** Resolve place-of-supply from this customer's state; omit to default INTRA. */
  customerId: UuidSchema.optional(),
  gstSlab: GstSlabSchema,
  /** Which billing entity issues the invoice (for the seller-state place-of-supply). */
  billingEntityId: UuidSchema.optional(),
  /** B2C/B2CF only: 1 = with service charge, 2 = without. */
  billType: z.union([z.literal(1), z.literal(2)]).optional(),
  /** Operator-entered sale amount (paise). */
  saleAmountPaise: z.string().regex(/^\d+$/),
  /** Running supplier payable total (paise). Defaults to 0. */
  supplierPayablePaise: z.string().regex(/^\d+$/).default('0'),
  /** DOM/INT - INT trips add TCS to the preview. */
  productType: z.enum(['DOM', 'INT']).optional(),
  /** Selected product codes - TCS needs a 2+ service-group "package". */
  products: z.array(z.string()).optional(),
  /** Optional override; otherwise derived from customer state vs seller. */
  placeOfSupply: GstPlaceOfSupplySchema.optional(),
});
export type InvoicePreviewRequest = z.infer<typeof InvoicePreviewRequestSchema>;

/**
 * Live GST preview response. All money is paise as a decimal STRING;
 * `taxableBasePaise` may be negative on a loss bill (legacy back-solve artifact).
 */
export const InvoicePreviewResponseSchema = z.object({
  placeOfSupply: GstPlaceOfSupplySchema,
  taxableBasePaise: z.string(),
  serviceChargePaise: z.string(),
  cgstPaise: z.string(),
  sgstPaise: z.string(),
  igstPaise: z.string(),
  grossTotalPaise: z.string(),
  /** 2% TCS on the pre-GST sale for INT trips, else 0. */
  tcsPaise: z.string(),
  /** grossTotal + tcs - what the customer actually pays. */
  grandTotalPaise: z.string(),
});
export type InvoicePreviewResponse = z.infer<typeof InvoicePreviewResponseSchema>;

// ----- Full create request (legacy createinvoicewithgst full-form parity) -----

/** Paise as a BigInt-safe decimal STRING (e.g. `1180000` = Rs 11,800.00). */
const PaiseStringSchema = z.string().regex(/^\d+$/);

/**
 * Full create-invoice payload - a faithful port of the legacy
 * `createinvoicewithgst.aspx` 11-section form. Persists the whole booking/trip
 * context, sectors, passengers, pricing breakdown, deposits and supplier detail
 * into the invoicing models. All money is paise as a decimal STRING; all dates
 * are ISO date strings.
 */
/** Why a supplier on an existing invoice line is being changed. */
export const SupplierChangeReasonSchema = z.enum(['MISTAKE', 'NOT_SERVICEABLE']);
export type SupplierChangeReason = z.infer<typeof SupplierChangeReasonSchema>;

export const CreateFullInvoiceRequestSchema = z.object({
  // 1. Product / GST info
  productType: z.enum(['DOM', 'INT']).optional(),
  products: z.array(z.object({ code: z.string(), name: z.string() })).default([]),
  gstSlab: GstSlabSchema,
  /** Which billing entity / GST account issues the invoice; omitted = the default. */
  billingEntityId: UuidSchema.optional(),
  /** Which company bank account prints on the invoice; omitted = the default. */
  bankAccountId: UuidSchema.optional(),
  /** B2C/B2CF service-charge mode: 1 = with service charge, 2 = without. */
  billType: z.union([z.literal(1), z.literal(2)]).optional(),
  placeOfSupply: GstPlaceOfSupplySchema.optional(),
  hotelLocationStateId: UuidSchema.optional(),
  /** Buyer GSTIN override (mandatory for corporate); also saved to the customer. */
  buyerGstin: z.string().optional(),
  isCorporate: z.boolean().optional(),
  corporateName: z.string().optional(),
  /** Show the CGST/SGST/IGST bifurcation on the printed invoice. Server defaults
   *  it on for CRP, off for retail slabs, when omitted. */
  showGstBifurcation: z.boolean().optional(),
  isGroupTour: z.boolean().optional(),
  groupTourName: z.string().optional(),

  // 2. Customer info
  customerId: UuidSchema,

  // 3. Booking info
  journeyType: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  departure: z.string().optional(),
  destination: z.string().optional(),
  departureDate: z.string().optional(),
  returnDate: z.string().optional(),
  hotelName: z.string().optional(),
  packageCombination: z.string().optional(),
  noOfPax: z.number().int().optional(),

  // 3b. Miscellaneous
  clientReference: z.string().optional(),
  leadGenerationDate: z.string().optional(),
  assignedAgentId: UuidSchema.optional(),
  branchId: UuidSchema.optional(),
  querySource: z.string().optional(),
  region: z.string().optional(),
  isTravelAgent: z.boolean().optional(),
  travelAgentName: z.string().optional(),

  // 4/6/7. Sectors
  flightMulticities: z
    .array(
      z.object({
        departureCity: z.string().optional(),
        destinationCity: z.string().optional(),
        departureDate: z.string().optional(),
      }),
    )
    .default([]),
  airlineSectors: z
    .array(
      z.object({
        paxName: z.string(),
        eTicket: z.string().optional(),
        pnr: z.string().optional(),
        airlineId: UuidSchema.optional(),
        airlineName: z.string().optional(),
        source: z.string().optional(),
        flightType: z.string().optional(),
        carrierType: z.string().optional(),
      }),
    )
    .default([]),
  trainBusSectors: z
    .array(
      z.object({
        pnr: z.string().optional(),
        bookingClass: z.string().optional(),
        quota: z.string().optional(),
      }),
    )
    .default([]),

  // 5. Passengers
  passengers: z
    .array(
      z.object({
        title: z.string().optional(),
        firstName: z.string().min(1),
        lastName: z.string().optional(),
        middleName: z.string().optional(),
        paxType: z.enum(['ADT', 'CHD', 'INF']).default('ADT'),
        dob: z.string().optional(),
        panNo: z.string().optional(),
        passportNumber: z.string().optional(),
        expiryDate: z.string().optional(),
        placeOfIssue: z.string().optional(),
        nationalityCountryId: UuidSchema.optional(),
      }),
    )
    .default([]),

  // 8. Pricing
  saleAmountPaise: PaiseStringSchema,
  basicPaise: PaiseStringSchema.optional(),
  yqPaise: PaiseStringSchema.optional(),
  seatChargePaise: PaiseStringSchema.optional(),
  mealChargePaise: PaiseStringSchema.optional(),
  taxesPaise: PaiseStringSchema.optional(),
  discountPercent: z.string().optional(),
  lineDescription: z.string().optional(),

  // 9. Payment / deposits
  paymentMode: z.string().optional(),
  clientPayingDate: z.string().optional(),
  deposits: z
    .array(
      z.object({
        amountPaise: PaiseStringSchema,
        balanceDueDate: z.string().optional(),
        isPaid: z.boolean().optional(),
      }),
    )
    .max(4)
    .default([]),

  // 10. Supplier detail
  supplierLines: z
    .array(
      z.object({
        /** Existing line id when editing; omitted for new lines. */
        id: UuidSchema.optional(),
        /** Why the supplier on an existing line changed (edit flow only). */
        supplierChangeReason: SupplierChangeReasonSchema.optional(),
        supplierId: UuidSchema.optional(),
        description: z.string().optional(),
        paymentDueDate: z.string().optional(),
        payableAmountPaise: PaiseStringSchema,
        commissionPaise: PaiseStringSchema.optional(),
        commissionCgstPaise: PaiseStringSchema.optional(),
        commissionSgstPaise: PaiseStringSchema.optional(),
        commissionIgstPaise: PaiseStringSchema.optional(),
        cgstPaise: PaiseStringSchema.optional(),
        sgstPaise: PaiseStringSchema.optional(),
        igstPaise: PaiseStringSchema.optional(),
        tdsPaise: PaiseStringSchema.optional(),
        currencyId: UuidSchema.optional(),
        exchangeRate: z.string().optional(),
        noOfCurrency: z.number().int().optional(),
      }),
    )
    .default([]),
});
export type CreateFullInvoiceRequest = z.infer<typeof CreateFullInvoiceRequestSchema>;

// ----- Edit data (GET /invoices/:id/edit-data) -----

/**
 * The editable state of an invoice, SHAPED to hydrate the full create-invoice
 * form in edit mode. A superset of `CreateFullInvoiceRequest`: every field the
 * form owns, plus display labels for the pickers (customer `displayName`,
 * airline `airlineName`). Money is paise as a decimal STRING; dates are ISO
 * strings or null. Only a PROFORMA invoice is editable (the frontend redirects
 * to the detail page otherwise, and the backend enforces it on save).
 *
 * `saleAmountPaise` echoes the stored `grossSalePaise`; for flight-only bills
 * the individual `basicPaise`/`yqPaise`/... components are restored so the
 * pricing recomputes identically. `middleName` has no persisted column, so it
 * always hydrates as null.
 */
export const InvoiceEditDataSchema = z.object({
  status: InvoiceStatusSchema,
  customerId: UuidSchema,
  // Contact details too, so the edit form's customer tile shows the phone and
  // email (same-name customers are only told apart by these).
  customer: z
    .object({
      id: UuidSchema,
      displayName: z.string(),
      kind: z.enum(['INDIVIDUAL', 'CORPORATE']).optional(),
      email: z.string().nullable().optional(),
      mobileNumber: z.string().nullable().optional(),
      companyName: z.string().nullable().optional(),
      gstNumber: z.string().nullable().optional(),
    })
    .nullable(),

  // 1. Product / GST info
  productType: z.enum(['DOM', 'INT']).nullable(),
  products: z.array(z.object({ code: z.string(), name: z.string() })),
  gstSlab: GstSlabSchema,
  billType: z.union([z.literal(1), z.literal(2)]).nullable(),
  placeOfSupply: GstPlaceOfSupplySchema,
  hotelLocationStateId: UuidSchema.nullable(),
  // Seller entity + the (optional) bank account chosen to print on the invoice,
  // and the buyer GSTIN - so re-opening the edit form restores the operator's
  // saved selections instead of falling back to defaults / blank.
  billingEntityId: UuidSchema.nullable(),
  bankAccountId: UuidSchema.nullable(),
  buyerGstin: z.string().nullable(),
  isCorporate: z.boolean(),
  corporateName: z.string().nullable(),
  showGstBifurcation: z.boolean(),
  isGroupTour: z.boolean(),
  groupTourName: z.string().nullable(),

  // 3. Booking info
  journeyType: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
  departure: z.string().nullable(),
  destination: z.string().nullable(),
  departureDate: z.string().nullable(),
  returnDate: z.string().nullable(),
  hotelName: z.string().nullable(),
  packageCombination: z.string().nullable(),
  noOfPax: z.number().int().nullable(),

  // 3b. Miscellaneous
  clientReference: z.string().nullable(),
  leadGenerationDate: z.string().nullable(),
  assignedAgentId: UuidSchema.nullable(),
  branchId: UuidSchema.nullable(),
  querySource: z.string().nullable(),
  region: z.string().nullable(),
  isTravelAgent: z.boolean(),
  travelAgentName: z.string().nullable(),

  // 4/6/7. Sectors
  flightMulticities: z.array(
    z.object({
      departureCity: z.string().nullable(),
      destinationCity: z.string().nullable(),
      departureDate: z.string().nullable(),
    }),
  ),
  airlineSectors: z.array(
    z.object({
      paxName: z.string(),
      eTicket: z.string().nullable(),
      pnr: z.string().nullable(),
      airlineId: UuidSchema.nullable(),
      airlineName: z.string().nullable(),
      source: z.string().nullable(),
      flightType: z.string().nullable(),
      carrierType: z.string().nullable(),
    }),
  ),
  trainBusSectors: z.array(
    z.object({
      pnr: z.string().nullable(),
      bookingClass: z.string().nullable(),
      quota: z.string().nullable(),
    }),
  ),

  // 5. Passengers
  passengers: z.array(
    z.object({
      title: z.string().nullable(),
      firstName: z.string(),
      middleName: z.string().nullable(),
      lastName: z.string().nullable(),
      paxType: z.enum(['ADT', 'CHD', 'INF']),
      dob: z.string().nullable(),
      panNo: z.string().nullable(),
      passportNumber: z.string().nullable(),
      expiryDate: z.string().nullable(),
      placeOfIssue: z.string().nullable(),
      nationalityCountryId: UuidSchema.nullable(),
    }),
  ),

  // 8. Pricing (money strings)
  saleAmountPaise: z.string(),
  basicPaise: z.string(),
  yqPaise: z.string(),
  seatChargePaise: z.string(),
  mealChargePaise: z.string(),
  taxesPaise: z.string(),
  discountPercent: z.string().nullable(),
  lineDescription: z.string().nullable(),

  // 9. Payment / deposits (only tranches with amount > 0)
  paymentMode: z.string().nullable(),
  clientPayingDate: z.string().nullable(),
  deposits: z.array(
    z.object({
      amountPaise: z.string(),
      balanceDueDate: z.string().nullable(),
      isPaid: z.boolean(),
    }),
  ),

  // 10. Supplier detail (money strings)
  supplierLines: z.array(
    z.object({
      id: UuidSchema.optional(),
      liveSettledPaise: z.string().optional(),
      livePaymentCount: z.number().int().optional(),
      supplierId: UuidSchema.nullable(),
      description: z.string().nullable(),
      paymentDueDate: z.string().nullable(),
      payableAmountPaise: z.string(),
      commissionPaise: z.string(),
      commissionCgstPaise: z.string(),
      commissionSgstPaise: z.string(),
      commissionIgstPaise: z.string(),
      cgstPaise: z.string(),
      sgstPaise: z.string(),
      igstPaise: z.string(),
      tdsPaise: z.string(),
      currencyId: UuidSchema.nullable(),
      exchangeRate: z.string().nullable(),
      noOfCurrency: z.number().int().nullable(),
    }),
  ),
});
export type InvoiceEditData = z.infer<typeof InvoiceEditDataSchema>;

// ----- Record receipt (POST /invoices/:id/receipts) -----

/**
 * Record a payment receipt against an invoice. The append-only receipt row
 * drives the invoice `amountPaidPaise` / `amountDuePaise`; when the running
 * paid total reaches the grand total on a PROFORMA invoice, the service
 * auto-converts it to a TAX_INVOICE (mints the tax number, flips the status).
 * Money is paise as a decimal STRING; `receiptDate` is an ISO date string.
 */
export const RecordReceiptRequestSchema = z.object({
  /** Payment amount in paise. */
  amountPaise: z.string().regex(/^\d+$/),
  /** ISO date (or datetime) the payment was received. */
  receiptDate: z.string().min(1),
  paymentMode: z.string().optional(),
  referenceNo: z.string().optional(),
  bankName: z.string().optional(),
  /** Which company bank account received the money (mandatory). */
  bankAccountId: UuidSchema,
  notes: z.string().optional(),
});
export type RecordReceiptRequest = z.infer<typeof RecordReceiptRequestSchema>;

/** Result of recording a receipt (post-conversion status + running totals). */
export const RecordReceiptResponseSchema = z.object({
  id: UuidSchema,
  status: InvoiceStatusSchema,
  amountPaidPaise: z.string(),
  amountDuePaise: z.string(),
  taxInvoiceNumber: z.string().nullable(),
});
export type RecordReceiptResponse = z.infer<typeof RecordReceiptResponseSchema>;

// ----- Manual proforma -> tax-invoice conversion -----

export const ConvertToTaxResultSchema = z.object({
  id: UuidSchema,
  status: InvoiceStatusSchema,
  taxInvoiceNumber: z.string(),
});
export type ConvertToTaxResult = z.infer<typeof ConvertToTaxResultSchema>;

/** Single convert request: optional issue date (yyyy-mm-dd; defaults to today). */
export const ConvertToTaxRequestSchema = z.object({
  issueDate: z.string().optional(),
});
export type ConvertToTaxRequest = z.infer<typeof ConvertToTaxRequestSchema>;

/** Bulk convert request: the proforma invoice ids to issue as tax invoices. */
export const BulkConvertRequestSchema = z.object({
  invoiceIds: z.array(UuidSchema).min(1).max(500),
  /** Issue date applied to all (yyyy-mm-dd; defaults to today). */
  issueDate: z.string().optional(),
});
export type BulkConvertRequest = z.infer<typeof BulkConvertRequestSchema>;

export const BulkConvertResultSchema = z.object({
  convertedCount: z.number().int(),
  skippedCount: z.number().int(),
  converted: z.array(z.object({ id: UuidSchema, taxInvoiceNumber: z.string() })),
  /** Ids skipped because they were not proformas (already issued or cancelled). */
  skipped: z.array(UuidSchema),
});
export type BulkConvertResult = z.infer<typeof BulkConvertResultSchema>;

// ----- Cancel invoice (legacy "isCancel" - full cancellation) -----

export const CancelInvoiceRequestSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type CancelInvoiceRequest = z.infer<typeof CancelInvoiceRequestSchema>;

export const CancelInvoiceResultSchema = z.object({
  id: UuidSchema,
  status: InvoiceStatusSchema,
});
export type CancelInvoiceResult = z.infer<typeof CancelInvoiceResultSchema>;

// ----- Ledger (GET /invoices/:id/ledger) -----

export const InvoiceLedgerSummarySchema = z.object({
  status: InvoiceStatusSchema,
  proformaNumber: z.string().nullable(),
  taxInvoiceNumber: z.string().nullable(),
  grandTotalPaise: z.string(),
  amountPaidPaise: z.string(),
  amountDuePaise: z.string(),
});
export type InvoiceLedgerSummary = z.infer<typeof InvoiceLedgerSummarySchema>;

export const InvoiceLedgerReceiptSchema = z.object({
  id: UuidSchema,
  /** UTZR receipt number, null for legacy receipts imported without one. */
  receiptNumber: z.string().nullable(),
  amountPaise: z.string(),
  receiptDate: IsoDateTimeSchema,
  paymentMode: z.string().nullable(),
  referenceNo: z.string().nullable(),
  bankName: z.string().nullable(),
  bankAccountId: UuidSchema.nullable(),
  notes: z.string().nullable(),
  /** Non-null when this receipt was voided (excluded from the paid total). */
  voidedAt: IsoDateTimeSchema.nullable(),
  /** Who recorded / voided this receipt (for the history view). */
  createdByName: z.string().nullable(),
  voidedByName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type InvoiceLedgerReceipt = z.infer<typeof InvoiceLedgerReceiptSchema>;

export const InvoiceLedgerEventSchema = z.object({
  id: UuidSchema,
  type: z.string(),
  summary: z.string().nullable(),
  /** Free-form JSON diff payload (null for events without a diff). */
  diff: z.unknown().nullable(),
  actorId: UuidSchema.nullable(),
  /** Display name of the actor, resolved from the user master. */
  actorName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type InvoiceLedgerEvent = z.infer<typeof InvoiceLedgerEventSchema>;

export const InvoiceLedgerResponseSchema = z.object({
  summary: InvoiceLedgerSummarySchema,
  receipts: z.array(InvoiceLedgerReceiptSchema),
  events: z.array(InvoiceLedgerEventSchema),
});
export type InvoiceLedgerResponse = z.infer<typeof InvoiceLedgerResponseSchema>;

// ----- Form options (GET /invoices/form-options) -----

/**
 * Static config the create-invoice form needs but which has no master table:
 * product lists (by domestic/international), GST slabs, payment modes, pax
 * types, titles and fare types. Airlines, suppliers, customers, branches,
 * agents, currencies and countries are fetched from their own endpoints.
 */
export const InvoiceFormOptionsSchema = z.object({
  products: z.object({
    DOM: z.array(z.object({ code: z.string(), name: z.string() })),
    INT: z.array(z.object({ code: z.string(), name: z.string() })),
  }),
  gstSlabs: z.array(z.object({ code: z.string(), name: z.string() })),
  paymentModes: z.array(z.string()),
  paxTypes: z.array(z.object({ code: z.string(), name: z.string() })),
  titles: z.array(z.string()),
  fareTypes: z.array(z.object({ code: z.number().int(), name: z.string() })),
});
export type InvoiceFormOptions = z.infer<typeof InvoiceFormOptionsSchema>;

// ===== v2: credit notes =====

export const CreditNoteKindSchema = z.enum(['FULL', 'PARTIAL']);
export type CreditNoteKind = z.infer<typeof CreditNoteKindSchema>;

export const CreditNoteStatusSchema = z.enum(['ISSUED', 'CANCELLED', 'USED']);
export type CreditNoteStatus = z.infer<typeof CreditNoteStatusSchema>;

/** Mark a credit note used (record-keeping): optional note + linked invoice. */
export const MarkCreditNoteUsedRequestSchema = z.object({
  note: z.string().max(500).optional(),
  appliedInvoiceId: UuidSchema.optional(),
});
export type MarkCreditNoteUsedRequest = z.infer<typeof MarkCreditNoteUsedRequestSchema>;

/**
 * Issue a credit note against a tax invoice. FULL credits the whole remaining
 * (un-credited) invoice total; PARTIAL credits `creditedAmountPaise` - a GROSS
 * amount (the refund to the customer, GST-inclusive). The taxable value and the
 * CGST/SGST/IGST reversed are derived by prorating the invoice off its grand
 * total. `creditedAmountPaise` is required and non-zero for a partial note,
 * ignored for a full one.
 */
export const CreateCreditNoteRequestSchema = z
  .object({
    kind: CreditNoteKindSchema,
    creditedAmountPaise: PaiseStringSchema.optional(),
    reason: z.string().min(1),
  })
  .refine(
    (v) => v.kind === 'FULL' || (v.creditedAmountPaise != null && v.creditedAmountPaise !== '0'),
    {
      message:
        'creditedAmountPaise is required and must be greater than 0 for a partial credit note',
      path: ['creditedAmountPaise'],
    },
  );
export type CreateCreditNoteRequest = z.infer<typeof CreateCreditNoteRequestSchema>;

export const CreditNoteRowSchema = z.object({
  id: UuidSchema,
  creditNoteNumber: z.string(),
  kind: CreditNoteKindSchema,
  status: CreditNoteStatusSchema,
  reason: z.string(),
  creditedTaxablePaise: z.string(),
  reversedCgstPaise: z.string(),
  reversedSgstPaise: z.string(),
  reversedIgstPaise: z.string(),
  totalPaise: z.string(),
  issuedAt: IsoDateTimeSchema,
  /** Set when marked used (record-keeping). */
  usedAt: IsoDateTimeSchema.nullable(),
  usedNote: z.string().nullable(),
});
export type CreditNoteRow = z.infer<typeof CreditNoteRowSchema>;

/** Everything a credit-note print sheet needs (buyer + entity + place + amounts). */
export const CreditNoteDetailSchema = CreditNoteRowSchema.extend({
  invoiceId: UuidSchema,
  invoiceNumber: z.string(),
  customerName: z.string().nullable(),
  customerGstin: z.string().nullable(),
  placeOfSupply: GstPlaceOfSupplySchema,
  gstSlab: GstSlabSchema,
  hsn: z.string(),
  billingEntity: InvoiceBillingEntitySchema,
  buyer: InvoiceBuyerSchema,
});
export type CreditNoteDetail = z.infer<typeof CreditNoteDetailSchema>;

/** Global credit-note list (finance listing, legacy creditNoteList). */
export const CreditNoteListQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  status: CreditNoteStatusSchema.optional(),
});
export type CreditNoteListQuery = z.infer<typeof CreditNoteListQuerySchema>;

export const CreditNoteListRowSchema = CreditNoteRowSchema.extend({
  invoiceId: UuidSchema,
  invoiceNumber: z.string(),
  customerName: z.string().nullable(),
});
export type CreditNoteListRow = z.infer<typeof CreditNoteListRowSchema>;

export const CreditNoteListSchema = z.object({
  rows: z.array(CreditNoteListRowSchema),
  /** Sum of ISSUED credit-note totals in the result. */
  totalPaise: z.string(),
});
export type CreditNoteList = z.infer<typeof CreditNoteListSchema>;

// ===== v2: supplier AP settlement =====

export const SupplierSettlementStatusSchema = z.enum(['UNPAID', 'PARTIAL', 'PAID']);
export type SupplierSettlementStatus = z.infer<typeof SupplierSettlementStatusSchema>;

/** Record a settlement (cash + TDS withheld) against one supplier payable line. */
export const RecordSupplierPaymentRequestSchema = z.object({
  amountPaise: PaiseStringSchema,
  tdsPaise: PaiseStringSchema.optional(),
  paymentDate: z.string().min(1),
  mode: z.string().optional(),
  reference: z.string().optional(),
  /** Which company bank account paid the supplier (mandatory). */
  bankAccountId: UuidSchema,
  note: z.string().optional(),
});
export type RecordSupplierPaymentRequest = z.infer<typeof RecordSupplierPaymentRequestSchema>;

export const RecordSupplierPaymentResponseSchema = z.object({
  id: UuidSchema,
  amountSettledPaise: z.string(),
  settlementStatus: SupplierSettlementStatusSchema,
  outstandingPaise: z.string(),
});
export type RecordSupplierPaymentResponse = z.infer<typeof RecordSupplierPaymentResponseSchema>;

export const SupplierPaymentRowSchema = z.object({
  id: UuidSchema,
  amountPaise: z.string(),
  tdsPaise: z.string(),
  paymentDate: IsoDateTimeSchema,
  mode: z.string().nullable(),
  reference: z.string().nullable(),
  bankAccountId: UuidSchema.nullable(),
  /** Display name of the bank account the payment was made from. */
  bankName: z.string().nullable(),
  note: z.string().nullable(),
  /** Non-null when this payment was voided (excluded from the settled total). */
  voidedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type SupplierPaymentRow = z.infer<typeof SupplierPaymentRowSchema>;

/** FIFO allocation of the line's settled total against a planned term. */
export const SupplierPaymentPlanTermStatusSchema = z.enum(['PENDING', 'PARTIAL', 'PAID']);
export type SupplierPaymentPlanTermStatus = z.infer<typeof SupplierPaymentPlanTermStatusSchema>;

/** One pre-planned installment term of a supplier payable line. */
export const SupplierPaymentPlanTermSchema = z.object({
  id: UuidSchema,
  termNo: z.number().int(),
  amountPaise: z.string(),
  dueDate: IsoDateTimeSchema.nullable(),
  note: z.string().nullable(),
  /** Cumulative-FIFO status: is the line's settled total enough to cover this term? */
  status: SupplierPaymentPlanTermStatusSchema,
  paidPaise: z.string(),
});
export type SupplierPaymentPlanTerm = z.infer<typeof SupplierPaymentPlanTermSchema>;

/** Replace-all save: the full set of installment terms for one supplier line. */
export const SaveSupplierPaymentPlanRequestSchema = z.object({
  terms: z
    .array(
      z.object({
        amountPaise: PaiseStringSchema,
        dueDate: z.string().optional(),
        note: z.string().optional(),
      }),
    )
    .max(24),
});
export type SaveSupplierPaymentPlanRequest = z.infer<typeof SaveSupplierPaymentPlanRequestSchema>;

/** One supplier payable line with its running settlement + payment history. */
export const SupplierLineSchema = z.object({
  id: UuidSchema,
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  description: z.string().nullable(),
  payablePaise: z.string(),
  amountSettledPaise: z.string(),
  outstandingPaise: z.string(),
  settlementStatus: SupplierSettlementStatusSchema,
  paymentDueDate: IsoDateTimeSchema.nullable(),
  payments: z.array(SupplierPaymentRowSchema),
  paymentPlan: z.array(SupplierPaymentPlanTermSchema),
  plannedTotalPaise: z.string(),
});
export type SupplierLine = z.infer<typeof SupplierLineSchema>;

/**
 * One transactional line in a supplier statement: a BILL (a supplier payable line
 * -> DR) or a PAYMENT (a settlement -> CR), with the running balance. Balance
 * convention (matches the client format): balance = previous - DR + CR.
 */
/** A refund owed back to us by a supplier after a not-serviceable supplier change. */
export const SupplierRefundRowSchema = z.object({
  id: UuidSchema,
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  invoiceSupplierId: UuidSchema,
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  amountPaise: z.string(),
  status: z.enum(['PENDING', 'RECEIVED']),
  receivedAt: IsoDateTimeSchema.nullable(),
  receivedAmountPaise: z.string().nullable(),
  receivedMode: z.string().nullable(),
  receivedReference: z.string().nullable(),
  bankAccountId: UuidSchema.nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type SupplierRefundRow = z.infer<typeof SupplierRefundRowSchema>;

export const SupplierRefundListQuerySchema = z.object({
  status: z.enum(['PENDING', 'RECEIVED']).optional(),
  supplierId: UuidSchema.optional(),
  invoiceId: UuidSchema.optional(),
  q: z.string().optional(),
});
export type SupplierRefundListQuery = z.infer<typeof SupplierRefundListQuerySchema>;

export const ReceiveSupplierRefundRequestSchema = z.object({
  receivedAmountPaise: PaiseStringSchema,
  receivedAt: z.string().min(1),
  receivedMode: z.string().optional(),
  receivedReference: z.string().optional(),
  bankAccountId: UuidSchema.optional(),
  note: z.string().optional(),
});
export type ReceiveSupplierRefundRequest = z.infer<typeof ReceiveSupplierRefundRequestSchema>;

export const SupplierLedgerRowSchema = z.object({
  kind: z.enum(['BILL', 'PAYMENT']),
  date: IsoDateTimeSchema,
  agentName: z.string().nullable(),
  customerName: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  narration: z.string().nullable(),
  /** Billed to us (debit); '0' on payment rows. */
  drPaise: z.string(),
  /** Paid by us (credit); '0' on bill rows. */
  crPaise: z.string(),
  /** Running balance after this row. */
  balancePaise: z.string(),
});
export type SupplierLedgerRow = z.infer<typeof SupplierLedgerRowSchema>;

/**
 * Supplier statement/ledger for one supplier over a period (client format):
 * a company + supplier header, opening balance, the DR/CR transaction rows with
 * a running balance, and the billed / paid / net-payable totals.
 */
export const SupplierLedgerSchema = z.object({
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  supplierAddress: z.string().nullable(),
  supplierGstin: z.string().nullable(),
  company: z.object({
    name: z.string().nullable(),
    address: z.string().nullable(),
    gstin: z.string().nullable(),
  }),
  fromDate: z.string().nullable(),
  toDate: z.string().nullable(),
  ledgerDate: IsoDateTimeSchema,
  openingBalancePaise: z.string(),
  totalBilledPaise: z.string(),
  totalPaidPaise: z.string(),
  netPayablePaise: z.string(),
  rows: z.array(SupplierLedgerRowSchema),
  /** PENDING refunds owed back by this supplier (receivable; not part of netPayable). */
  pendingRefunds: z.array(SupplierRefundRowSchema).optional(),
  pendingRefundsPaise: z.string().optional(),
});
export type SupplierLedger = z.infer<typeof SupplierLedgerSchema>;

export const SupplierOutstandingRowSchema = z.object({
  invoiceSupplierId: UuidSchema,
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  paymentDueDate: IsoDateTimeSchema.nullable(),
  payablePaise: z.string(),
  amountSettledPaise: z.string(),
  outstandingPaise: z.string(),
  settlementStatus: SupplierSettlementStatusSchema,
});
export type SupplierOutstandingRow = z.infer<typeof SupplierOutstandingRowSchema>;

export const SupplierOutstandingReportSchema = z.object({
  rows: z.array(SupplierOutstandingRowSchema),
  totalOutstandingPaise: z.string(),
  /** PENDING supplier refunds (receivables) in the same scope. */
  pendingRefunds: z.array(SupplierRefundRowSchema).optional(),
  pendingRefundsPaise: z.string().optional(),
});
export type SupplierOutstandingReport = z.infer<typeof SupplierOutstandingReportSchema>;

// ----- Supplier payment status (every payable line + its paid/unpaid status) -----

export const SupplierPaymentStatusQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  supplierId: UuidSchema.optional(),
  /** PAID / PARTIAL / UNPAID; omit for all. */
  status: SupplierSettlementStatusSchema.optional(),
  /** Free-text search: supplier / customer / invoice / proforma number. */
  q: z.string().trim().optional(),
});
export type SupplierPaymentStatusQuery = z.infer<typeof SupplierPaymentStatusQuerySchema>;

export const SupplierPaymentStatusRowSchema = z.object({
  invoiceSupplierId: UuidSchema,
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  proformaNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  invoiceDate: IsoDateTimeSchema,
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  agentName: z.string().nullable(),
  payablePaise: z.string(),
  amountSettledPaise: z.string(),
  outstandingPaise: z.string(),
  settlementStatus: SupplierSettlementStatusSchema,
});
export type SupplierPaymentStatusRow = z.infer<typeof SupplierPaymentStatusRowSchema>;

export const SupplierPaymentStatusReportSchema = z.object({
  rows: z.array(SupplierPaymentStatusRowSchema),
  totalPayablePaise: z.string(),
  totalSettledPaise: z.string(),
  totalOutstandingPaise: z.string(),
});
export type SupplierPaymentStatusReport = z.infer<typeof SupplierPaymentStatusReportSchema>;

/** Supplier payments report (what we HAVE paid, legacy supplierpaymentreport). */
export const SupplierPaymentReportQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  supplierId: UuidSchema.optional(),
});
export type SupplierPaymentReportQuery = z.infer<typeof SupplierPaymentReportQuerySchema>;

export const SupplierPaymentReportRowSchema = z.object({
  id: UuidSchema,
  paymentDate: IsoDateTimeSchema,
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  amountPaise: z.string(),
  tdsPaise: z.string(),
  mode: z.string().nullable(),
  reference: z.string().nullable(),
});
export type SupplierPaymentReportRow = z.infer<typeof SupplierPaymentReportRowSchema>;

export const SupplierPaymentReportSchema = z.object({
  rows: z.array(SupplierPaymentReportRowSchema),
  totalPaidPaise: z.string(),
  totalTdsPaise: z.string(),
});
export type SupplierPaymentReport = z.infer<typeof SupplierPaymentReportSchema>;

/**
 * Working-capital schedule: every PLANNED supplier installment term across
 * invoices, one row per term, aged into buckets by due date so upcoming
 * outflows are visible. `paidPaise` is the FIFO allocation of the parent line's
 * settled total against this term.
 */
export const SupplierPaymentScheduleQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  supplierId: UuidSchema.optional(),
  /** Hide terms already fully covered by settlement. */
  pendingOnly: z.coerce.boolean().optional(),
});
export type SupplierPaymentScheduleQuery = z.infer<typeof SupplierPaymentScheduleQuerySchema>;

export const SupplierPaymentScheduleBucketSchema = z.enum([
  'OVERDUE',
  'THIS_WEEK',
  'THIS_MONTH',
  'LATER',
  'UNSCHEDULED',
]);
export type SupplierPaymentScheduleBucket = z.infer<typeof SupplierPaymentScheduleBucketSchema>;

export const SupplierPaymentScheduleRowSchema = z.object({
  termId: UuidSchema,
  invoiceSupplierId: UuidSchema,
  invoiceId: UuidSchema,
  invoiceNumber: z.string().nullable(),
  supplierId: UuidSchema.nullable(),
  supplierName: z.string().nullable(),
  description: z.string().nullable(),
  termNo: z.number().int(),
  dueDate: IsoDateTimeSchema.nullable(),
  amountPaise: z.string(),
  paidPaise: z.string(),
  outstandingPaise: z.string(),
  status: SupplierPaymentPlanTermStatusSchema,
  bucket: SupplierPaymentScheduleBucketSchema,
});
export type SupplierPaymentScheduleRow = z.infer<typeof SupplierPaymentScheduleRowSchema>;

export const SupplierPaymentScheduleTotalsSchema = z.object({
  bucket: SupplierPaymentScheduleBucketSchema,
  amountPaise: z.string(),
  outstandingPaise: z.string(),
});
export type SupplierPaymentScheduleTotals = z.infer<typeof SupplierPaymentScheduleTotalsSchema>;

export const SupplierPaymentScheduleSchema = z.object({
  rows: z.array(SupplierPaymentScheduleRowSchema),
  byBucket: z.array(SupplierPaymentScheduleTotalsSchema),
  totalPlannedPaise: z.string(),
  totalOutstandingPaise: z.string(),
});
export type SupplierPaymentSchedule = z.infer<typeof SupplierPaymentScheduleSchema>;

// ===== v2: billing-entity settings (invoice seller identity) =====

/** The invoice seller identity, edited from Settings and rendered on every invoice. */
export const BillingEntitySettingsSchema = z.object({
  id: UuidSchema,
  legalName: z.string(),
  gstin: z.string(),
  pan: z.string().nullable(),
  cin: z.string().nullable(),
  logoUrl: z.string().nullable(),
  sealUrl: z.string().nullable(),
  terms: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  stateId: UuidSchema.nullable(),
  stateName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  invoicePrefix: z.string(),
  hsnDefault: z.string(),
});
export type BillingEntitySettings = z.infer<typeof BillingEntitySettingsSchema>;

export const UpdateBillingEntitySettingsSchema = z.object({
  legalName: z.string().min(1),
  gstin: z.string().min(1),
  pan: z.string().nullish(),
  cin: z.string().nullish(),
  logoUrl: z.string().nullish(),
  sealUrl: z.string().nullish(),
  terms: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  city: z.string().nullish(),
  postalCode: z.string().nullish(),
  stateId: UuidSchema.nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  invoicePrefix: z.string().min(1),
  hsnDefault: z.string().min(1),
});
export type UpdateBillingEntitySettings = z.infer<typeof UpdateBillingEntitySettingsSchema>;

/** A GST account (billing entity) option - for the invoice form + settings list. */
export const BillingEntityOptionSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  legalName: z.string(),
  gstin: z.string(),
  isDefault: z.boolean(),
});
export type BillingEntityOption = z.infer<typeof BillingEntityOptionSchema>;

/** Create a new billing entity (GST account). Same fields as the update, plus code. */
export const CreateBillingEntitySchema = UpdateBillingEntitySettingsSchema.extend({
  code: z.string().min(1),
  isDefault: z.boolean().optional(),
});
export type CreateBillingEntity = z.infer<typeof CreateBillingEntitySchema>;

// ===== Bank accounts (multi-bank: pick per invoice + per payment) =====

/** One company bank account, for the settings manager + dropdowns. */
export const BankAccountSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  ifsc: z.string().nullable(),
  branchName: z.string().nullable(),
  isDefault: z.boolean(),
  /** Printed on invoices as part of the default "all banks" set when no bank is chosen. */
  showOnInvoice: z.boolean(),
  isActive: z.boolean(),
});
export type BankAccount = z.infer<typeof BankAccountSchema>;

export const CreateBankAccountSchema = z.object({
  label: z.string().min(1),
  bankName: z.string().min(1),
  accountNumber: z.string().min(1),
  ifsc: z.string().optional(),
  branchName: z.string().optional(),
  isDefault: z.boolean().optional(),
  showOnInvoice: z.boolean().optional(),
});
export type CreateBankAccount = z.infer<typeof CreateBankAccountSchema>;

export const UpdateBankAccountSchema = CreateBankAccountSchema.partial();
export type UpdateBankAccount = z.infer<typeof UpdateBankAccountSchema>;

// ===== v2: GST output register =====

export const GstOutputRegisterQuerySchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  billingEntityId: UuidSchema.optional(),
  /** Comma-separated GstPlaceOfSupply values (multi-select). */
  placeOfSupply: z.string().trim().optional(),
  // Cross-screen filters: free-text search + agent(s) + B2B/B2C.
  q: z.string().trim().optional(),
  /** Comma-separated agent names (multi-select). */
  travelAgentName: z.string().trim().optional(),
  customerType: z.enum(['B2B', 'B2C']).optional(),
});
export type GstOutputRegisterQuery = z.infer<typeof GstOutputRegisterQuerySchema>;

/**
 * One register line, shaped to the client's GST sale-register format. Money is a
 * signed decimal STRING - credit-note rows carry NEGATIVE amounts (they reduce
 * output tax), so these are plain strings, not the unsigned PaiseString used for
 * inbound money. Rates are percent strings (e.g. "9", "18", "0").
 */
export const GstOutputRegisterRowSchema = z.object({
  kind: z.enum(['INVOICE', 'CREDIT_NOTE']),
  // Both numbers so the register shows the proforma and its GST invoice number.
  proformaNumber: z.string().nullable(),
  gstInvoiceNumber: z.string().nullable(),
  // Primary display number (GST number if issued, else proforma; credit-note number).
  number: z.string(),
  date: IsoDateTimeSchema,
  customerName: z.string().nullable(),
  customerType: z.enum(['B2B', 'B2C']),
  customerGstin: z.string().nullable(),
  placeOfSupply: GstPlaceOfSupplySchema,
  // POS as the customer's state name (the client's "POS (Customer State)" column).
  posState: z.string().nullable(),
  serviceType: z.string().nullable(),
  gstSlab: GstSlabSchema,
  hsn: z.string(),
  nonTaxablePaise: z.string(), // supplier value (non-taxable portion)
  taxablePaise: z.string(), // service charge (the taxable margin)
  cgstRate: z.string(),
  cgstPaise: z.string(),
  sgstRate: z.string(),
  sgstPaise: z.string(),
  igstRate: z.string(),
  igstPaise: z.string(),
  totalGstPaise: z.string(),
  grossSalePaise: z.string(),
  /** Invoice id for INVOICE rows (so the row can be marked GST-paid); null for
   * credit-note rows, which are not individually markable. */
  invoiceId: UuidSchema.nullable(),
  /** Per-invoice GST paid status (false for credit-note rows). */
  gstPaid: z.boolean(),
  /** When the invoice's GST was marked paid (ISO), or null. */
  gstPaidAt: IsoDateTimeSchema.nullable(),
});
export type GstOutputRegisterRow = z.infer<typeof GstOutputRegisterRowSchema>;

/** PATCH body for the per-invoice GST paid flag (mirror of the TCS one). */
export const MarkGstInvoicePaidRequestSchema = z.object({
  paid: z.boolean(),
  paidAt: IsoDateTimeSchema.optional(),
});
export type MarkGstInvoicePaidRequest = z.infer<typeof MarkGstInvoicePaidRequestSchema>;

export const GstOutputRegisterSummaryRowSchema = z.object({
  label: z.string(),
  taxablePaise: z.string(),
  cgstPaise: z.string(),
  sgstPaise: z.string(),
  igstPaise: z.string(),
  totalPaise: z.string(),
});
export type GstOutputRegisterSummaryRow = z.infer<typeof GstOutputRegisterSummaryRowSchema>;

export const GstOutputRegisterSchema = z.object({
  rows: z.array(GstOutputRegisterRowSchema),
  bySlab: z.array(GstOutputRegisterSummaryRowSchema),
  byPlaceOfSupply: z.array(GstOutputRegisterSummaryRowSchema),
  /** Net output tax after subtracting credit notes. */
  totals: GstOutputRegisterSummaryRowSchema,
});
export type GstOutputRegister = z.infer<typeof GstOutputRegisterSchema>;

// ===== v2: TCS register =====

export const TcsRegisterQuerySchema = z.object({
  from: z.string().min(1),
  to: z.string().min(1),
  billingEntityId: UuidSchema.optional(),
  q: z.string().trim().optional(),
  /** Comma-separated agent names (multi-select). */
  travelAgentName: z.string().trim().optional(),
});
export type TcsRegisterQuery = z.infer<typeof TcsRegisterQuerySchema>;

/**
 * One TCS-register line (client format): the invoice on which TCS was collected,
 * its quarter/month, the customer + PAN, the base it was charged on, the rate,
 * and the TCS value, plus the combined challan (if any) covering it.
 */
export const TcsRegisterRowSchema = z.object({
  clientName: z.string().nullable(),
  invoiceNumber: z.string(),
  date: IsoDateTimeSchema,
  quarter: z.string(),
  month: z.string(),
  pan: z.string().nullable(),
  baseAmountPaise: z.string(),
  ratePercent: z.string(),
  tcsPaise: z.string(),
  nature: z.string(),
  agentName: z.string().nullable(),
  /** Invoice id, so the register can select it into a combined challan. */
  invoiceId: UuidSchema,
  /** Combined challan grouping this invoice (null = not on a challan). */
  tcsChallanId: UuidSchema.nullable(),
  tcsChallanNo: z.string().nullable(),
  /** Deposit date of the linked challan (ISO date-time at UTC midnight). */
  tcsChallanDate: IsoDateTimeSchema.nullable(),
  /** Per-invoice TCS paid status (independent of any challan). */
  tcsPaid: z.boolean(),
  /** When the invoice's TCS was marked paid (ISO), or null. */
  tcsPaidAt: IsoDateTimeSchema.nullable(),
  /** Count of this invoice's TCS certificate documents (not soft-deleted). */
  tcsCertificateCount: z.number().int().nonnegative(),
});
export type TcsRegisterRow = z.infer<typeof TcsRegisterRowSchema>;

export const TcsRegisterSchema = z.object({
  rows: z.array(TcsRegisterRowSchema),
  totalBasePaise: z.string(),
  totalTcsPaise: z.string(),
});
export type TcsRegister = z.infer<typeof TcsRegisterSchema>;

// ===== TCS combined challans =====

export const TcsDocumentKindSchema = z.enum(['CHALLAN', 'CERTIFICATE', 'OTHER']);
export type TcsDocumentKind = z.infer<typeof TcsDocumentKindSchema>;

export const TcsChallanEventTypeSchema = z.enum([
  'CREATED',
  'UPDATED',
  'MARKED_PAID',
  'MARKED_UNPAID',
  'INVOICE_ADDED',
  'INVOICE_REMOVED',
  'DOCUMENT_ADDED',
  'DOCUMENT_REMOVED',
]);
export type TcsChallanEventType = z.infer<typeof TcsChallanEventTypeSchema>;

const ChallanNoSchema = z.string().trim().max(100);

export const CreateTcsChallanRequestSchema = z.object({
  invoiceIds: z.array(UuidSchema).min(1).max(500),
  challanNo: ChallanNoSchema.optional(),
  challanDate: IsoDateTimeSchema.optional(),
  note: z.string().trim().max(1000).optional(),
});
export type CreateTcsChallanRequest = z.infer<typeof CreateTcsChallanRequestSchema>;

/** PATCH body: mark paid/unpaid and/or edit challan fields. */
export const UpdateTcsChallanRequestSchema = z.object({
  paid: z.boolean().optional(),
  paidAt: IsoDateTimeSchema.optional(),
  challanNo: ChallanNoSchema.optional(),
  challanDate: IsoDateTimeSchema.optional(),
  note: z.string().trim().max(1000).optional(),
});
export type UpdateTcsChallanRequest = z.infer<typeof UpdateTcsChallanRequestSchema>;

export const TcsChallanInvoicesRequestSchema = z.object({
  invoiceIds: z.array(UuidSchema).min(1).max(500),
});
export type TcsChallanInvoicesRequest = z.infer<typeof TcsChallanInvoicesRequestSchema>;

export const TcsChallanListQuerySchema = z.object({
  paid: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  q: z.string().trim().optional(),
});
export type TcsChallanListQuery = z.infer<typeof TcsChallanListQuerySchema>;

export const TcsDocumentRowSchema = z.object({
  id: UuidSchema,
  /** Set when the document hangs off a challan (the challan receipt). */
  challanId: UuidSchema.nullable(),
  /** Set when the document hangs off an invoice (its TCS certificate). */
  invoiceId: UuidSchema.nullable(),
  kind: TcsDocumentKindSchema,
  fileName: z.string(),
  contentType: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
  createdAt: IsoDateTimeSchema,
  downloadUrl: z.string(),
});
export type TcsDocumentRow = z.infer<typeof TcsDocumentRowSchema>;

/** PATCH body for the per-invoice TCS paid flag (independent of any challan). */
export const MarkTcsInvoicePaidRequestSchema = z.object({
  paid: z.boolean(),
  paidAt: IsoDateTimeSchema.optional(),
});
export type MarkTcsInvoicePaidRequest = z.infer<typeof MarkTcsInvoicePaidRequestSchema>;

export const TcsChallanInvoiceSchema = z.object({
  invoiceId: UuidSchema,
  invoiceNumber: z.string(),
  customerName: z.string().nullable(),
  tcsPaise: z.string(),
});
export type TcsChallanInvoice = z.infer<typeof TcsChallanInvoiceSchema>;

export const TcsChallanEventSchema = z.object({
  id: UuidSchema,
  type: TcsChallanEventTypeSchema,
  summary: z.string(),
  actorId: UuidSchema.nullable(),
  actorName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type TcsChallanEvent = z.infer<typeof TcsChallanEventSchema>;

export const TcsChallanSchema = z.object({
  id: UuidSchema,
  challanNo: z.string().nullable(),
  challanDate: IsoDateTimeSchema.nullable(),
  paid: z.boolean(),
  paidAt: IsoDateTimeSchema.nullable(),
  note: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  invoiceCount: z.number().int().nonnegative(),
  totalTcsPaise: z.string(),
  documentCount: z.number().int().nonnegative(),
});
export type TcsChallan = z.infer<typeof TcsChallanSchema>;

export const TcsChallanDetailSchema = TcsChallanSchema.extend({
  invoices: z.array(TcsChallanInvoiceSchema),
  documents: z.array(TcsDocumentRowSchema),
  /** Audit timeline, newest first. */
  events: z.array(TcsChallanEventSchema),
});
export type TcsChallanDetail = z.infer<typeof TcsChallanDetailSchema>;

export const TcsDocumentPresignRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(150),
  kind: TcsDocumentKindSchema,
});
export type TcsDocumentPresignRequest = z.infer<typeof TcsDocumentPresignRequestSchema>;

export const TcsDocumentPresignResponseSchema = z.object({
  key: z.string(),
  uploadUrl: z.string(),
  expiresAt: IsoDateTimeSchema,
});
export type TcsDocumentPresignResponse = z.infer<typeof TcsDocumentPresignResponseSchema>;

export const CreateTcsDocumentRequestSchema = z.object({
  key: z.string().min(1),
  fileName: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(150),
  sizeBytes: z.number().int().nonnegative().max(26214400).optional(),
  kind: TcsDocumentKindSchema,
});
export type CreateTcsDocumentRequest = z.infer<typeof CreateTcsDocumentRequestSchema>;

// ===== Agent Report Card (report) =====

/**
 * Agent Report Card: a per-source (or per-agent) monthly pivot of sales
 * performance across one financial year (Apr-Mar), with domestic/international
 * and retail/corporate splits. Rebuild of the legacy `agentreportcard.aspx`.
 * Money is BigInt paise as a decimal string; months run April-first.
 */
export const AgentReportCardModeSchema = z.enum([
  'CONSOLIDATED_LEAD', // rows = query source, all agents
  'CONSOLIDATED_USER', // rows = agent (user), all agents
  'AGENT', // rows = query source, scoped to one agent
]);
export type AgentReportCardMode = z.infer<typeof AgentReportCardModeSchema>;

export const AgentReportCardQuerySchema = z.object({
  /** Financial-year start year, e.g. 2026 for FY 2026-2027. */
  fyStartYear: z.coerce.number().int().min(2000).max(2100),
  mode: AgentReportCardModeSchema.default('CONSOLIDATED_LEAD'),
  /** Required when mode = AGENT: the agent name (as stored on the invoice). */
  agentName: z.string().optional(),
  /** Scope to retail (non-corporate) invoices only - the Monthly Sale base. */
  retailOnly: z.coerce.boolean().optional(),
});
export type AgentReportCardQuery = z.infer<typeof AgentReportCardQuerySchema>;

/** One sale/profit/pax split cell (UTAZZO/CORP x DOM/INT). */
export const AgentReportCardSplitSchema = z.object({
  salePaise: z.string(),
  profitPaise: z.string(),
  pax: z.number().int(),
});
export type AgentReportCardSplit = z.infer<typeof AgentReportCardSplitSchema>;

/** All metrics for one calendar month of one row dimension. */
export const AgentReportCardMonthSchema = z.object({
  /** Calendar month 1-12. */
  month: z.number().int().min(1).max(12),
  salePaise: z.string(),
  profitPaise: z.string(),
  invoiceCount: z.number().int(),
  pendingCount: z.number().int(),
  voidCount: z.number().int(),
  /**
   * Operation Efficiency amount. INTERIM definition (realized margin: margin of
   * invoices fully collected that month) pending the legacy formula. Isolated so
   * it can be redefined in one place.
   */
  opEffPaise: z.string(),
  pax: z.number().int(),
  utzDom: AgentReportCardSplitSchema,
  utzInt: AgentReportCardSplitSchema,
  corpDom: AgentReportCardSplitSchema,
  corpInt: AgentReportCardSplitSchema,
});
export type AgentReportCardMonth = z.infer<typeof AgentReportCardMonthSchema>;

export const AgentReportCardRowSchema = z.object({
  /** 'ALL' grand-total row, or a query-source / agent name. */
  dimension: z.string(),
  isGrandTotal: z.boolean(),
  /** 12 entries in financial-year order (April first, March last). */
  months: z.array(AgentReportCardMonthSchema),
});
export type AgentReportCardRow = z.infer<typeof AgentReportCardRowSchema>;

export const AgentReportCardResponseSchema = z.object({
  fyStartYear: z.number().int(),
  /** Display label, e.g. "2026-2027". */
  fyLabel: z.string(),
  mode: AgentReportCardModeSchema,
  /** Calendar month (1-12) that is the "current month" column, or null if the FY is not the current one. */
  currentMonth: z.number().int().nullable(),
  /** Table A: ALL grand-total row + one row per query source (or agent). */
  rows: z.array(AgentReportCardRowSchema),
  /** Table B: ALL row + one row per legacy product category. */
  categoryRows: z.array(AgentReportCardRowSchema),
  /** Distinct agent names in this FY, for the Agent dropdown. */
  agents: z.array(z.string()),
});
export type AgentReportCardResponse = z.infer<typeof AgentReportCardResponseSchema>;

// ============================================================================
// Bank Book reconciliation
//
// Upload the "Bank Book" working sheet (one row per bank txn: Cr = money in,
// Dr = money out, with Invoice No / Customer / Supplier / Heads) and reconcile
// it against what the system already records:
//   - a Cr row against an invoice   -> customer receipt  (InvoiceReceipt)
//   - a Dr "Supplier Payment" row   -> supplier payment  (SupplierPayment)
// Non-invoice rows (contra / expense / loan / opening balance) are reported as
// N/A and skipped. Matching is on invoice number + amount. On commit we CREATE
// the missing entries and UPDATE the mismatched ones.
// ============================================================================

export const BankReconcileKindSchema = z.enum(['RECEIPT', 'SUPPLIER']);
export type BankReconcileKind = z.infer<typeof BankReconcileKindSchema>;

export const BankReconcileStatusSchema = z.enum([
  /** An existing entry already records this invoice + amount. Nothing to do. */
  'MATCHED',
  /** Invoice found + a single recorded entry whose amount differs -> update it. */
  'MISMATCH',
  /** Invoice found, no entry records this amount -> create it. */
  'MISSING',
  /** The invoice number is not in the system. */
  'INVOICE_NOT_FOUND',
  /** The invoice number (or supplier payable line) resolves to several rows. */
  'INVOICE_AMBIGUOUS',
  /** Contra / expense / loan / opening balance: no invoice to reconcile. */
  'NON_INVOICE',
]);
export type BankReconcileStatus = z.infer<typeof BankReconcileStatusSchema>;

export const BankReconcileActionSchema = z.enum(['none', 'create', 'update', 'skip']);
export type BankReconcileAction = z.infer<typeof BankReconcileActionSchema>;

export const BankReconcileRowSchema = z.object({
  /** 0-based index among the parsed data rows; stable id the commit refers to. */
  rowIndex: z.number().int(),
  /** 1-based worksheet row number, for display. */
  excelRow: z.number().int(),
  kind: BankReconcileKindSchema.nullable(),
  heads: z.string().nullable(),
  /** ISO date parsed from the sheet. */
  date: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  /** Resolved invoice UUID (null when not found / ambiguous / non-invoice). */
  invoiceId: z.string().nullable(),
  /** Resolved supplier payable line UUID (SUPPLIER rows only). */
  supplierLineId: z.string().nullable(),
  /** Customer name (RECEIPT) or supplier name (SUPPLIER) from the sheet. */
  counterparty: z.string().nullable(),
  /** Short display of the matched invoice (number + customer), for the table. */
  invoiceLabel: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Mapped company bank account UUID, when the narration names a known bank. */
  bankAccountId: z.string().nullable(),
  bankNarration: z.string().nullable(),
  /** Cr (receipt) or Dr (supplier payment) as paise. */
  amountPaise: z.string(),
  status: BankReconcileStatusSchema,
  /** Default action the commit will take unless the row is skipped. */
  action: BankReconcileActionSchema,
  /** Matched / mismatched entry UUID (receipt or supplier payment). */
  existingId: z.string().nullable(),
  existingAmountPaise: z.string().nullable(),
  /** Human reason for the status. */
  note: z.string().nullable(),
});
export type BankReconcileRow = z.infer<typeof BankReconcileRowSchema>;

export const BankReconcileSummarySchema = z.object({
  total: z.number().int(),
  matched: z.number().int(),
  mismatch: z.number().int(),
  missing: z.number().int(),
  notFound: z.number().int(),
  ambiguous: z.number().int(),
  nonInvoice: z.number().int(),
});
export type BankReconcileSummary = z.infer<typeof BankReconcileSummarySchema>;

export const BankReconcilePreviewResponseSchema = z.object({
  fileName: z.string(),
  sheet: z.string(),
  summary: BankReconcileSummarySchema,
  rows: z.array(BankReconcileRowSchema),
});
export type BankReconcilePreviewResponse = z.infer<typeof BankReconcilePreviewResponseSchema>;

export const BankReconcileUploadRequestSchema = z.object({
  fileName: z.string().min(1),
  /** Base64-encoded .xlsx workbook. */
  fileBase64: z.string().min(1),
});
export type BankReconcileUploadRequest = z.infer<typeof BankReconcileUploadRequestSchema>;

export const BankReconcileCommitRowSchema = z.object({
  kind: BankReconcileKindSchema,
  action: z.enum(['create', 'update']),
  /** Invoice UUID (RECEIPT rows); carried for the paid/due recompute. */
  invoiceId: z.string(),
  /** Supplier payable line UUID (SUPPLIER rows). */
  supplierLineId: z.string().nullable(),
  /** Receipt / supplier-payment UUID being updated (required for 'update'). */
  existingId: z.string().nullable(),
  amountPaise: z.string(),
  date: z.string().nullable(),
  bankName: z.string().nullable(),
  bankAccountId: z.string().nullable(),
  reference: z.string().nullable(),
  counterparty: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
});
export type BankReconcileCommitRow = z.infer<typeof BankReconcileCommitRowSchema>;

export const BankReconcileCommitRequestSchema = z.object({
  rows: z.array(BankReconcileCommitRowSchema),
  /** Original upload file name / sheet, recorded on the reconciliation run log. */
  fileName: z.string().optional(),
  sheet: z.string().optional(),
});
export type BankReconcileCommitRequest = z.infer<typeof BankReconcileCommitRequestSchema>;

export const BankReconcileCommitResultSchema = z.object({
  created: z.number().int(),
  updated: z.number().int(),
  failed: z.number().int(),
  errors: z.array(z.object({ invoiceNumber: z.string().nullable(), message: z.string() })),
  /** Id of the persisted reconciliation run (null if none was recorded). */
  runId: UuidSchema.nullable().optional(),
});
export type BankReconcileCommitResult = z.infer<typeof BankReconcileCommitResultSchema>;

// ===== Bank reconciliation run history =====

/** One applied row in a recorded reconciliation run (for the detail view). */
export const BankReconcileRunDetailRowSchema = z.object({
  kind: BankReconcileKindSchema,
  action: z.enum(['create', 'update']),
  invoiceNumber: z.string().nullable(),
  counterparty: z.string().nullable(),
  amountPaise: z.string(),
  date: z.string().nullable(),
  bankName: z.string().nullable(),
  /** Whether this row was applied successfully. */
  result: z.enum(['ok', 'failed']),
  error: z.string().nullable(),
});
export type BankReconcileRunDetailRow = z.infer<typeof BankReconcileRunDetailRowSchema>;

/** A recorded bank reconciliation run (one commit). */
export const BankReconcileRunSchema = z.object({
  id: UuidSchema,
  fileName: z.string(),
  sheet: z.string().nullable(),
  /** Rows applied in this commit (create + update). */
  totalRows: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  failed: z.number().int(),
  createdById: UuidSchema.nullable(),
  createdByName: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type BankReconcileRun = z.infer<typeof BankReconcileRunSchema>;

export const BankReconcileRunListSchema = z.object({
  runs: z.array(BankReconcileRunSchema),
});
export type BankReconcileRunList = z.infer<typeof BankReconcileRunListSchema>;

export const BankReconcileRunDetailSchema = BankReconcileRunSchema.extend({
  rows: z.array(BankReconcileRunDetailRowSchema),
});
export type BankReconcileRunDetail = z.infer<typeof BankReconcileRunDetailSchema>;
